import { Chip, Link } from '@mui/material'
import { i18WithDefault as t } from '@shared/locale'
import { HeadCell, Row, SharedTable, InvoiceStatus } from 'components-ui'
import { useMemo } from 'react'
import { Link as NavLink } from 'react-router-dom'
import { getCustomInvoicePetNames } from 'service-api/src/mappers'
import { TCustomInvoiceDto } from 'service-api/src/types/general'

const PURCHASE_DATE = 'updatedAt',
  SERVICE = 'service',
  PETS = 'pets',
  STATUS = 'status',
  SERVICE_DATES = 'serviceDates',
  ACTIONS = 'actions'

const headCells: HeadCell[] = [
  { id: SERVICE_DATES, label: t('global.label.visitDates'), sortable: true },
  { id: SERVICE, label: t('global.label.visit') },
  { id: PETS, label: t('global.label.pets') },
  { id: STATUS, label: t('global.label.status') },
  { id: PURCHASE_DATE, label: t('global.label.purchaseDate'), sortable: true },
  { id: ACTIONS, label: t('global.label.actions') },
]

export interface BookingListProps {
  accountName: string
  invoices: TCustomInvoiceDto[]
  isLoading: boolean
  hasMore: boolean
  loadMore: () => void
  onSort: (sortColumn: string, sortOrder: 'asc' | 'desc', limit: number) => void
  onRowsPerPageChange?: (limit: number) => void
  defaultSortColumn?: string
  defaultSortOrder?: 'asc' | 'desc'
  initialRowsPerPage?: number
}

export const BookingList = ({
  accountName,
  invoices,
  isLoading,
  hasMore,
  loadMore,
  onSort,
  onRowsPerPageChange,
  defaultSortColumn = PURCHASE_DATE,
  defaultSortOrder = 'desc',
  initialRowsPerPage,
}: BookingListProps) => {
  const rows: Row[] = useMemo(() => {
    return invoices.map((invoice) => ({
      [SERVICE_DATES]: invoice.serviceDate,
      [SERVICE]: invoice.service ? <Chip label={invoice.service} /> : null,
      [PETS]: getCustomInvoicePetNames(invoice),
      [STATUS]: <InvoiceStatus invoice={invoice} />,
      [PURCHASE_DATE]: invoice.purchaseDate,
      [ACTIONS]: (
        <Link
          component={NavLink}
          to={`/${accountName}/account/bookings/${invoice.orderId}`}
          variant="button"
          color="primary"
          underline="none"
          target="_blank"
        >
          {t('purchaseHistory.viewSummary')}
        </Link>
      ),
    }))
  }, [invoices, accountName])

  return (
    <SharedTable
      rows={rows}
      head={headCells}
      defaultOrder={defaultSortOrder}
      defaultOrderBy={defaultSortColumn}
      initialRowsPerPage={initialRowsPerPage}
      isLoading={isLoading}
      emptyTableMessage={t('purchaseHistory.emptyTable')}
      hasMore={hasMore}
      loadMore={loadMore}
      onSort={onSort}
      onChangeRowsPerPage={onRowsPerPageChange}
    />
  )
}
